import type { AgentApiProfitsItem } from '@/types/serializers';
import { formatProfitDisplayDate } from './formatDisplayDate';

function hasProfitDate(value: string | null | undefined) {
  return value != null && value !== '';
}

/**
 * 売上一覧カード左列の日付行（先頭の本文 + 任意のグレーラベル）
 *
 * - 支払い前・入社前・入社日未設定 → 本文のみ「入社日未定」
 * - 支払い前・入社前・入社日あり → 日付 + 「入社日」
 * - 支払い前・入社確定（入金待ち）・予定日あり → 日付 + 「支払い予定日」
 * - 支払い前・入社確定（入金待ち）・予定日なし → 本文のみ「支払い予定日未定」
 */
export function getProfitCardDateLine(profit: AgentApiProfitsItem): {
  primary: string;
  label: string | null;
} {
  switch (profit.status) {
    case 'waiting_entry':
      if (!hasProfitDate(profit.entry_expect_date)) {
        return { primary: '入社日未定', label: null };
      }
      return {
        primary: formatProfitDisplayDate(profit.entry_expect_date),
        label: '入社日',
      };
    case 'invoice_sent':
      if (!hasProfitDate(profit.invoice_sent_date)) {
        return { primary: '請求書送付日未定', label: null };
      }
      return {
        primary: formatProfitDisplayDate(profit.invoice_sent_date),
        label: '請求書送付日',
      };
    case 'waiting_payment':
      if (!hasProfitDate(profit.payment_date)) {
        return { primary: '支払い予定日未定', label: null };
      }
      return {
        primary: formatProfitDisplayDate(profit.payment_date),
        label: '支払い予定日',
      };
    case 'paid':
      return {
        primary: formatProfitDisplayDate(profit.payment_confirmed_date),
        label: '入金確定日',
      };
    case 'refund':
      return {
        primary: formatProfitDisplayDate(profit.refund_date),
        label: '返金予定日',
      };
    case 'refunded':
      return {
        primary: formatProfitDisplayDate(profit.refund_confirmed_date),
        label: '返金確定日',
      };
    case 'candidate_declined':
    case 'company_declined':
    case 'deleted':
      if (!hasProfitDate(profit.entry_expect_date)) {
        return { primary: '入社日未定', label: null };
      }
      return {
        primary: formatProfitDisplayDate(profit.entry_expect_date),
        label: '入社日',
      };
    default: {
      // 上記以外（型に無い値が API から来た場合）でも一覧が落ちないようにする。
      // 新しい ProfitStatus を追加したときは上の case を追記し、default に来ないようにすること。
      const _exhaustive: never = profit.status;
      return { primary: '', label: null };
    }
  }
}
